import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/components/ui/toast';
import { useCategoriesQuery } from '@/hooks/use-categories';
import { useUpdateLink } from '@/hooks/use-links';
import { describeError } from '@/lib/api-client';
import type { LinkDetail, UpdateLinkBody } from '@/types/api';

/**
 * Edits the human-facing fields of a link. Categories are edited here too — the API records a
 * manual override so a later re-enrichment does not silently undo the admin's decision.
 */
export function LinkEditDialog({
  link,
  open,
  onOpenChange,
}: {
  link: LinkDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const categories = useCategoriesQuery();
  const updateLink = useUpdateLink();
  const { toast } = useToast();

  const [title, setTitle] = useState(link.title ?? '');
  const [description, setDescription] = useState(link.description ?? '');
  const [selected, setSelected] = useState<string[]>(link.categories);

  // Reset the form whenever the dialog is (re)opened for a link.
  useEffect(() => {
    if (!open) return;
    setTitle(link.title ?? '');
    setDescription(link.description ?? '');
    setSelected(link.categories);
  }, [open, link]);

  const available = (categories.data ?? []).filter(
    (category) => !category.archived || selected.includes(category.slug),
  );

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const body: UpdateLinkBody = {
      title: title.trim(),
      description: description.trim(),
      categories: [...selected].sort(),
    };
    updateLink.mutate(
      { linkId: link.linkId, body },
      {
        onSuccess: () => {
          toast({ title: 'Link updated', variant: 'success' });
          onOpenChange(false);
        },
        onError: (error) =>
          toast({
            title: 'Could not update the link',
            description: describeError(error),
            variant: 'error',
          }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Edit link</DialogTitle>
          <DialogDescription>
            Overrides the metadata extracted during enrichment. The original URL cannot be changed.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-link-title">Title</Label>
            <Input
              id="edit-link-title"
              value={title}
              maxLength={300}
              onChange={(event) => setTitle(event.target.value)}
              placeholder={link.domain}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="edit-link-description">Description</Label>
            <Textarea
              id="edit-link-description"
              value={description}
              // Keep in sync with updateLinkSchema.description in @slc/validation.
              maxLength={2000}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Short summary shown on the card."
            />
          </div>

          <fieldset className="flex flex-col gap-2">
            <legend className="mb-1 text-sm font-medium">Categories</legend>
            {categories.isPending ? (
              <p className="text-sm text-muted-foreground">Loading categories…</p>
            ) : available.length === 0 ? (
              <p className="text-sm text-muted-foreground">No categories have been created yet.</p>
            ) : (
              <ul className="grid max-h-56 grid-cols-1 gap-1 overflow-y-auto sm:grid-cols-2">
                {available.map((category) => {
                  const id = `edit-link-category-${category.slug}`;
                  return (
                    <li key={category.categoryId}>
                      <label
                        htmlFor={id}
                        className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                      >
                        <Checkbox
                          id={id}
                          checked={selected.includes(category.slug)}
                          onCheckedChange={(checked) =>
                            setSelected((current) =>
                              checked === true
                                ? [...current, category.slug]
                                : current.filter((slug) => slug !== category.slug),
                            )
                          }
                        />
                        <span className="min-w-0 truncate">{category.name}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </fieldset>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={updateLink.isPending}>
              Save changes
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
